import { useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import { IconSelectorInput } from '@@common';

import { ConfirmationDialogModel, TableColumnActionModel } from '../../Models/TableColumnModel';

type ActionsInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	actions: TableColumnActionModel[] | undefined;
	onActionsChange: (actions: TableColumnActionModel[] | undefined) => void;
};

const ACTION_TYPES = ['Link', 'Button'] as const;

export function ActionsInputDialog({ open, setOpen, actions, onActionsChange }: ActionsInputDialogProps) {
	const [localActions, setLocalActions] = useState<TableColumnActionModel[]>(actions || []);

	const handleAddAction = () => {
		setLocalActions([
			...localActions,
			{
				Type: 'Link',
				Label: 'New Action',
			},
		]);
	};

	const handleDeleteAction = (index: number) => {
		setLocalActions(localActions.filter((_, i) => i !== index));
	};

	const handleActionChange = (index: number, field: keyof TableColumnActionModel, value: any) => {
		const updated = [...localActions];
		updated[index] = { ...updated[index], [field]: value };
		setLocalActions(updated);
	};

	const handleConfirmationToggle = (index: number, checked: boolean) => {
		const action = localActions[index];
		const updated = [...localActions];
		updated[index] = {
			...action,
			ConfirmationDialog: checked ? { Title: '', Message: '' } : undefined,
		};
		setLocalActions(updated);
	};

	const handleConfirmationChange = (index: number, field: keyof ConfirmationDialogModel, value: string) => {
		const action = localActions[index];
		const updated = [...localActions];
		updated[index] = {
			...action,
			ConfirmationDialog: { ...(action.ConfirmationDialog ?? { Title: '', Message: '' }), [field]: value },
		};
		setLocalActions(updated);
	};

	const handleSave = () => {
		onActionsChange(localActions.length > 0 ? localActions : undefined);
		setOpen(false);
	};

	const handleCancel = () => {
		setLocalActions(actions || []);
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
			<DialogTitle>Configure Actions</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					{localActions.length === 0 && (
						<Typography variant="body2" color="textSecondary">
							No actions configured. Click "Add Action" to create one.
						</Typography>
					)}

					{localActions.map((action, index) => (
						<Paper key={index} variant="outlined" sx={{ p: 2 }}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Typography variant="subtitle2">Action {index + 1}</Typography>
									<IconButton size="small" onClick={() => handleDeleteAction(index)} color="error">
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Box>

								<Stack direction="row" spacing={2}>
									<FormControl size="small" sx={{ minWidth: 120 }}>
										<InputLabel>Type</InputLabel>
										<Select label="Type" value={action.Type} onChange={(e) => handleActionChange(index, 'Type', e.target.value)}>
											{ACTION_TYPES.map((type) => (
												<MenuItem key={type} value={type}>
													{type}
												</MenuItem>
											))}
										</Select>
									</FormControl>

									<TextField
										label="Label"
										value={action.Label}
										onChange={(e) => handleActionChange(index, 'Label', e.target.value)}
										size="small"
										required
										sx={{ flex: 1 }}
									/>

									<Box sx={{ minWidth: 200 }}>
										<IconSelectorInput value={action.IconName} onValueChange={(value) => handleActionChange(index, 'IconName', value)} label="Icon" />
									</Box>
								</Stack>

								<TextField
									label="Path"
									value={action.Path || ''}
									onChange={(e) => handleActionChange(index, 'Path', e.target.value || undefined)}
									size="small"
									fullWidth
									placeholder="/path/{Id}"
									helperText="Use {FieldName} for dynamic values from the row data"
								/>

								<Box>
									<FormControlLabel
										control={
											<Checkbox
												checked={action.IsDefault ?? false}
												onChange={(e) => handleActionChange(index, 'IsDefault', e.target.checked || undefined)}
												size="small"
											/>
										}
										label="Default action"
									/>
									<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5, ml: 4 }}>
										Triggered automatically when a user clicks on a table row
									</Typography>
								</Box>

								<Box>
									<FormControlLabel
										control={
											<Checkbox checked={!!action.ConfirmationDialog} onChange={(e) => handleConfirmationToggle(index, e.target.checked)} size="small" />
										}
										label="Requires confirmation"
									/>
									<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5, ml: 4 }}>
										Show a confirmation dialog before executing this action
									</Typography>
								</Box>

								{action.ConfirmationDialog && (
									<Stack spacing={1.5}>
										<TextField
											label="Confirmation Title"
											value={action.ConfirmationDialog.Title}
											onChange={(e) => handleConfirmationChange(index, 'Title', e.target.value)}
											size="small"
											fullWidth
											required
										/>
										<TextField
											label="Confirmation Message"
											value={action.ConfirmationDialog.Message}
											onChange={(e) => handleConfirmationChange(index, 'Message', e.target.value)}
											size="small"
											fullWidth
											required
											multiline
											rows={2}
										/>
									</Stack>
								)}
							</Stack>
						</Paper>
					))}

					<Button startIcon={<AddCircleIcon />} onClick={handleAddAction} variant="outlined">
						Add Action
					</Button>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
